import { useState } from 'react';

import toast from '@liquidweb-dev/undercurrent/toast';
import { useSelector } from 'react-redux';

import appConfigSelectors from 'modules/appConfig/selectors';
import useBillingPaymentProfileDetails from 'modules/queries/billing/payment/profile/useDetails';

interface AcceptDispatchRequestI {
	authData: Record<string, string>;
	cardData: Record<string, string>;
}

export interface AcceptDispatchOkI {
	opaqueData: {
		dataDescriptor: string;
		dataValue: string;
	};
	messages: {
		resultCode: 'Ok';
		message: {
			code: string;
			text: string;
		}[];
	};
}

interface AcceptDispatchErrorI {
	messages: {
		resultCode: 'Error';
		message: {
			code: string;
			text: string;
		}[];
	};
}

type AcceptDispatchResultI = AcceptDispatchOkI | AcceptDispatchErrorI;

declare global {
	interface Window {
		Accept: {
			dispatchData: (
				request: AcceptDispatchRequestI,
				callback: (res: AcceptDispatchResultI) => void,
			) => void;
		};
	}
}

const isAcceptResultOk = (
	result: AcceptDispatchResultI,
): result is AcceptDispatchOkI => {
	return result.messages.resultCode === 'Ok';
};

const dispatchDataAsync = async (secureData: AcceptDispatchRequestI) => {
	const result = await new Promise<AcceptDispatchResultI>((resolve) => {
		window.Accept.dispatchData(secureData, resolve);
	});
	return result;
};

/** @see https://developer.authorize.net/api/reference/features/acceptjs.html#Appendix_Error_Codes */
const acceptJsErrorCodes = {
	cardNumber: ['E_WC_05'],
	expirationDate: ['E_WC_06', 'E_WC_07', 'E_WC_08'],
};

const useAcceptJs = () => {
	const { merchantKeys, fallbackMerchant } = useSelector(
		appConfigSelectors.getAcceptJsConfig,
	);
	const [isSubmittingAcceptJs, setIsSubmittingAcceptJs] = useState(false);

	const {
		data: billingPaymentProfileDetails,
		isLoading: isLoadingBillingPaymentProfileDetails,
		isError: isErrorBillingPaymentProfileDetails,
	} = useBillingPaymentProfileDetails({});
	const { merchant_id: merchantId } = billingPaymentProfileDetails || {};
	const merchantKey = merchantId && merchantKeys[merchantId];

	const submitAcceptJs = async (values: {
		cardNumber: string;
		expirationDate: string;
		cardCode: string;
	}) => {
		const { cardNumber, expirationDate, cardCode } = values;
		const [expMonth, expYear] = expirationDate.split('/');

		const errors: {
			merchant?: string;
			cardNumber?: string;
			expirationDate?: string;
		} = {};

		const { id: fallbackMerchantId, key: fallbackMerchantKey } =
			fallbackMerchant;

		const secureData = {
			authData: {
				apiLoginID: merchantId || fallbackMerchantId,
				clientKey: merchantKey || fallbackMerchantKey,
			},
			cardData: {
				cardNumber: cardNumber.replace(/\s/g, ''),
				month: expMonth,
				year: expYear,
				cardCode,
			},
		};

		// Create secure data with authorize.net
		setIsSubmittingAcceptJs(true);
		const acceptResult = await dispatchDataAsync(secureData);
		setIsSubmittingAcceptJs(false);

		// utilize AcceptJs error messages
		if (!isAcceptResultOk(acceptResult)) {
			acceptResult.messages.message.forEach((message) => {
				if (acceptJsErrorCodes.cardNumber.includes(message.code)) {
					errors.cardNumber = message.text;
					return;
				}

				if (acceptJsErrorCodes.expirationDate.includes(message.code)) {
					errors.expirationDate = message.text;
					return;
				}

				// toast for any other error codes
				toast.danger(message.text);
			});

			return { errors };
		}

		return { opaqueData: acceptResult.opaqueData };
	};

	return {
		/** true if attempting to fetch opaque data */
		isSubmittingAcceptJs,
		/** function to fetch opaque data */
		submitAcceptJs,
		/** true if ready to submit AcceptJs data */
		readyToSubmitAcceptJs:
			!isLoadingBillingPaymentProfileDetails &&
			!isErrorBillingPaymentProfileDetails,
	} as const;
};

export default useAcceptJs;
